<template>
  <HrPageLayout
    title="Supervisor Appraisals"
    subtitle="Review targets, mid-year and end-year ratings for your team"
    icon="👥"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Performance Management' }, { label: 'Supervisor' }]"
  >
    <div class="sup">
      <div class="filters">
        <select v-model="statusFilter" @change="load">
          <option value="">All statuses</option>
          <option v-for="s in statusOptions" :key="s" :value="s">{{ formatStatus(s) }}</option>
        </select>
        <button class="hr-btn hr-btn-secondary" @click="load">Refresh</button>
      </div>

      <div v-if="!selectedId">
        <div v-if="loading" class="muted pad">Loading…</div>
        <table v-else class="tbl">
          <thead>
            <tr>
              <th>Employee</th>
              <th>PF / Job</th>
              <th>Department</th>
              <th>Cycle</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in list" :key="a.id">
              <td><strong>{{ a.employee_name }}</strong></td>
              <td>{{ a.emp_no || a.employee_id }} · {{ a.job_title || '—' }}</td>
              <td>{{ a.department_name || '—' }}</td>
              <td>{{ a.cycle_name }}</td>
              <td><span class="st">{{ formatStatus(a.status) }}</span></td>
              <td><button class="hr-btn hr-btn-sm hr-btn-primary" @click="open(a.id)">Open</button></td>
            </tr>
            <tr v-if="!list.length"><td colspan="6" class="muted pad">No team appraisals assigned to you.</td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="workspace">
        <div class="ws-head">
          <button class="hr-btn hr-btn-ghost" @click="selectedId=null; detail=null">← Back</button>
          <div>
            <h3>{{ detail?.assignment?.employee_name }} — {{ detail?.assignment?.cycle_name }}</h3>
            <p>{{ formatStatus(detail?.assignment?.status) }} · Form {{ formLabel }} · Emp/Sup/Agreed: {{ detail?.assignment?.endyear_emp_score ?? '—' }} / {{ detail?.assignment?.endyear_sup_score ?? '—' }} / {{ detail?.assignment?.endyear_agreed_score ?? '—' }}</p>
          </div>
        </div>

        <div v-if="!detail" class="muted pad">Loading…</div>
        <template v-else>
          <div class="panel">
            <h4>Targets</h4>
            <div v-for="t in targets" :key="t.id || t._i" class="tgt">
              <strong>{{ t.kra || t.objective }}</strong>
              <p class="meta">Weight {{ t.weight }}% · Indicator: {{ t.indicator || '—' }} · Target: {{ t.annual_target || '—' }}</p>
              <p>{{ t.objective }}</p>
              <p v-if="t.emp_comments" class="muted">Employee: {{ t.emp_comments }}</p>
              <label>Supervisor comments on target
                <textarea v-model="t.sup_comments" rows="2" :disabled="!canReviewTargets"></textarea>
              </label>
            </div>
            <div class="actions" v-if="canReviewTargets">
              <button class="hr-btn hr-btn-success" :disabled="saving" @click="approveTargets">Agree &amp; approve targets</button>
              <button class="hr-btn hr-btn-outline-danger" :disabled="saving" @click="rejectTargets">Return for revision</button>
            </div>
            <p v-if="canReviewTargets" class="agree-hint">Discuss each target with the employee. Agreement locks weights for the rest of the cycle.</p>
          </div>

          <div class="panel" v-if="showMid">
            <h4>Mid-year — appraiser &amp; agreed ratings</h4>
            <div v-for="t in targets" :key="'m'+t.id" class="tgt">
              <strong>{{ t.kra }}</strong>
              <p class="meta">Appraisee mid rating: {{ t.mid_emp_rating ?? '—' }} · Progress: {{ t.mid_progress || '—' }}</p>
              <div class="g2">
                <label>Appraiser rating (1–5) <input type="number" min="1" max="5" step="0.1" v-model.number="t.mid_sup_rating"></label>
                <label>Agreed rating <input type="number" min="1" max="5" step="0.1" v-model.number="t.mid_agreed_rating"></label>
                <label class="full">Supervisor comments <textarea v-model="t.mid_sup_comment" rows="2"></textarea></label>
              </div>
            </div>
            <button class="hr-btn hr-btn-primary" :disabled="saving" @click="saveMid">Save mid-year ratings</button>
          </div>

          <div class="panel" v-if="showEnd">
            <h4>End-year — appraiser then agreed scores</h4>
            <div v-for="t in targets" :key="'e'+t.id" class="tgt">
              <strong>{{ t.kra }}</strong>
              <p class="meta">Self-assessment: {{ t.end_progress || '—' }} · Appraisee rating: {{ t.end_emp_rating ?? '—' }} (retained after agreed)</p>
              <div class="g2">
                <label>Appraiser rating (1–5) <input type="number" min="1" max="5" step="0.1" v-model.number="t.end_sup_rating"></label>
                <label>Agreed rating (1–5) <input type="number" min="1" max="5" step="0.1" v-model.number="t.end_agreed_rating"></label>
                <label class="full">Supervisor comment <textarea v-model="t.end_sup_comment" rows="2"></textarea></label>
              </div>
            </div>
            <label>Overall supervisor comments <textarea v-model="supComments" rows="3"></textarea></label>
            <label>Development / training needs <textarea v-model="developmentNeeds" rows="2"></textarea></label>
            <label>Recommendations next cycle <textarea v-model="nextCycleRecs" rows="2"></textarea></label>
            <div class="actions">
              <button class="hr-btn hr-btn-primary" :disabled="saving" @click="saveAppraiser">Save appraiser ratings</button>
              <button class="hr-btn hr-btn-success" :disabled="saving" @click="saveAgreed">Save agreed scores</button>
              <button class="hr-btn hr-btn-outline-primary" :disabled="saving" @click="confirmSup">Supervisor confirmation</button>
              <button class="hr-btn hr-btn-secondary" :disabled="saving" @click="completeMidYear" v-if="showMid">Mark mid-year complete</button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

const STATUS_OPTIONS = [
  "not_started", "target_setting_in_progress", "awaiting_supervisor_review", "targets_approved",
  "midyear_review_due", "midyear_review_completed", "endyear_self_assessment_due",
  "awaiting_appraiser_rating", "awaiting_agreement", "under_review", "completed", "disputed", "overdue"
];

export default {
  name: "PasSupervisorQueue",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      list: [],
      statusFilter: "",
      statusOptions: STATUS_OPTIONS,
      selectedId: null,
      detail: null,
      targets: [],
      supComments: "",
      developmentNeeds: "",
      nextCycleRecs: ""
    };
  },
  computed: {
    formLabel() {
      const a = this.detail?.assignment;
      if (!a) return "—";
      return a.form_type === "job_group_j_above" ? (a.form_j_above_label || "J+") : (a.form_h_below_label || "H−");
    },
    canReviewTargets() {
      return this.detail?.assignment?.status === "awaiting_supervisor_review";
    },
    showMid() {
      const st = this.detail?.assignment?.status || "";
      return ["targets_approved", "midyear_review_due", "midyear_review_completed", "endyear_self_assessment_due"].includes(st)
        || st.includes("midyear");
    },
    showEnd() {
      const st = this.detail?.assignment?.status || "";
      return [
        "endyear_self_assessment_due", "awaiting_appraiser_rating", "awaiting_agreement",
        "under_review", "completed", "disputed"
      ].includes(st) || (this.targets || []).some((t) => t.end_emp_rating != null);
    }
  },
  mounted() { this.load(); },
  methods: {
    formatStatus(s) { return String(s || "").replace(/_/g, " "); },
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasGetSupervisorQueue");
        if (this.statusFilter) fd.append("status", this.statusFilter);
        const res = await execute(fd, authUrl);
        let rows = res?.data?.data?.assignments || [];
        if (this.statusFilter) rows = rows.filter((r) => r.status === this.statusFilter);
        this.list = rows;
      } finally { this.loading = false; }
    },
    async open(id) {
      this.selectedId = id;
      const fd = new FormData();
      fd.append("function", "pasGetAssignment");
      fd.append("id", String(id));
      const res = await execute(fd, authUrl);
      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Load failed");
        return;
      }
      this.detail = res.data.data;
      this.targets = (this.detail.targets || []).map((t) => ({ ...t }));
      this.supComments = this.detail.assignment?.sup_comments || "";
      this.developmentNeeds = this.detail.assignment?.development_needs || "";
      this.nextCycleRecs = this.detail.assignment?.next_cycle_recommendations || "";
    },
    async approveTargets() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasApproveTargets");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("comments", this.targets.map((t) => t.sup_comments).filter(Boolean).join("\n") || "");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Targets approved");
          await this.open(this.selectedId);
          this.load();
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async rejectTargets() {
      const reason = window.prompt("Return reason for the employee:");
      if (!reason) return;
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasRejectTargets");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("reason", reason);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Returned to employee");
          await this.open(this.selectedId);
          this.load();
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async saveMid() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveMidYearReview");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Mid-year saved");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async saveAppraiser() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveEndYearAppraiser");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("sup_comments", this.supComments);
        fd.append("development_needs", this.developmentNeeds);
        fd.append("next_cycle_recommendations", this.nextCycleRecs);
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Appraiser ratings saved (self-ratings retained)");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async saveAgreed() {
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasSaveAgreedRatings");
        fd.append("assignment_id", String(this.selectedId));
        fd.append("targets", JSON.stringify(this.targets));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Agreed scores recorded");
          await this.open(this.selectedId);
        } else toast.error(res?.data?.message || "Failed");
      } finally { this.saving = false; }
    },
    async completeMidYear() {
      const fd = new FormData();
      fd.append("function", "pasCompleteMidYear");
      fd.append("assignment_id", String(this.selectedId));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Mid-year completed");
        await this.open(this.selectedId);
        this.load();
      } else toast.error(res?.data?.message || "Failed");
    },
    async confirmSup() {
      const fd = new FormData();
      fd.append("function", "pasConfirmAppraisal");
      fd.append("assignment_id", String(this.selectedId));
      fd.append("role", "supervisor");
      fd.append("note", "Supervisor confirmation");
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Confirmed");
        await this.open(this.selectedId);
      } else toast.error(res?.data?.message || "Failed");
    }
  }
};
</script>

<style scoped>
.filters { display:flex; gap:.5rem; margin-bottom:1rem; flex-wrap:wrap; }
.filters select { border:1px solid #e2e8f0; border-radius:8px; padding:.45rem .7rem; min-width:200px; }
.tbl { width:100%; border-collapse:collapse; background:#fff; border:1px solid #e2e8f0; border-radius:12px; overflow:hidden; font-size:.88rem; }
.tbl th, .tbl td { padding:.7rem .85rem; border-bottom:1px solid #f1f5f9; text-align:left; }
.tbl th { background:#f8fafc; font-size:.72rem; text-transform:uppercase; color:#64748b; }
.st { text-transform:capitalize; font-weight:700; font-size:.8rem; color:#0369a1; }
.ws-head { display:flex; gap:.75rem; margin-bottom:1rem; align-items:flex-start; }
.ws-head h3 { margin:0; font-size:1.05rem; }
.ws-head p { margin:.2rem 0 0; font-size:.85rem; color:#64748b; }
.panel { background:#fff; border:1px solid #e2e8f0; border-radius:14px; padding:1rem; margin-bottom:1rem; }
.panel h4 { margin:0 0 .75rem; font-size:.95rem; }
.tgt { border:1px solid #e2e8f0; border-radius:10px; padding:.75rem; margin-bottom:.75rem; background:#fafbfc; }
.meta { font-size:.8rem; color:#64748b; }
.g2 { display:grid; grid-template-columns:1fr 1fr; gap:.55rem; }
.g2 label, .tgt label, .panel > label { display:flex; flex-direction:column; gap:.25rem; font-size:.75rem; font-weight:700; color:#475569; }
.full { grid-column:1/-1; }
input, textarea { border:1px solid #e2e8f0; border-radius:8px; padding:.45rem .6rem; }
.actions { display:flex; flex-wrap:wrap; gap:.5rem; margin-top:.5rem; }
.agree-hint { font-size:.82rem; color:#0369a1; margin-top:.5rem; }
.hr-btn-ghost { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:.4rem .7rem; cursor:pointer; }
.muted { color:#94a3b8; }
.pad { padding:1.5rem; }
@media (max-width:720px) { .g2 { grid-template-columns:1fr; } }
</style>
